import {
  EventStreamContentType,
  fetchEventSource,
} from '@microsoft/fetch-event-source'
import type { GeckoLocale } from 'types/measurement/geckoLocale'
import { z } from 'zod'

import {
  type DocChatChartAckRequest,
  DocChatChartAckResponseSchema,
  type DocChatSearchResult,
  type DocChatSseEvent,
  isDocChatKeepaliveSseEvent,
  readDocChatSseEvent,
} from './documentSearchApiSchemas'

export const DOC_CHAT_START_PATH = '/api/v2/documents/chat/start'
export const DOC_CHAT_STOP_PATH = '/api/v2/documents/chat/stop'
export const DOC_CHAT_CHART_ACK_PATH = '/api/v2/documents/chat/chart-ack'

const stopResponseSchema = z.object({
  stopped: z.literal(true),
  completed: z.boolean(),
})

export async function postDocChatStop({
  url,
  authorization,
  conversationId,
  siteId,
  turnId,
}: {
  url: string
  authorization: string
  conversationId: string
  siteId: string
  turnId: string
}): Promise<boolean> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: JSON.stringify({
      conversation_id: conversationId,
      site_id: siteId,
      turn_id: turnId,
    }),
    signal: AbortSignal.timeout(15000),
  })
  if (!response.ok) throw new Error('DocChat stop was not acknowledged')
  const parsed = stopResponseSchema.safeParse(await response.json())
  if (!parsed.success) {
    throw new Error('DocChat stop was not acknowledged')
  }
  return parsed.data.completed
}

/** Tell the running turn what the chart did with a command; false when nothing was waiting. */
export async function postDocChatChartAck({
  url,
  body,
  authorization,
}: {
  url: string
  body: DocChatChartAckRequest
  authorization: string
}): Promise<boolean> {
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
  })
  if (!response.ok) return false
  const parsed = DocChatChartAckResponseSchema.safeParse(await response.json())
  return parsed.success && parsed.data.accepted
}

export class DocChatTurnAlreadyRunningError extends Error {
  override name = 'DocChatTurnAlreadyRunningError'
}

export async function postDocChatStream({
  url,
  body,
  authorization,
  locale,
  signal,
  onEvent,
  onResponseReceived,
}: {
  url: string
  body: string
  authorization: string
  locale?: GeckoLocale
  signal?: AbortSignal
  onEvent?: (event: DocChatSseEvent) => void
  onResponseReceived?: (response: DocChatSearchResult) => void
}): Promise<DocChatSearchResult> {
  let terminalEvent:
    | Extract<DocChatSseEvent, { type: 'response' | 'error' }>
    | undefined
  await fetchEventSource(url, {
    method: 'POST',
    body,
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      Authorization: authorization,
      ...(locale ? { 'Accept-Language': locale } : {}),
    },
    openWhenHidden: true,
    signal,
    async onopen(response) {
      if (response.status === 409) {
        throw new DocChatTurnAlreadyRunningError()
      }
      if (!response.ok) {
        throw new Error(`Doc chat stream failed (${response.status})`)
      }
      const contentType = response.headers.get('content-type')
      if (!contentType?.startsWith(EventStreamContentType)) {
        throw new Error(
          `Expected content-type to be ${EventStreamContentType}, Actual: ${contentType}`
        )
      }
    },
    onmessage(rawEvent) {
      const event = readDocChatSseEvent(rawEvent.data)
      if (!event) {
        return
      }
      if (isDocChatKeepaliveSseEvent(event)) {
        onEvent?.(event)
        return
      }
      // The worker saves the turn after emitting its answer and closes the
      // stream once it releases the running-turn lock. Sending earlier gets 409.
      terminalEvent = event
      if (event.type === 'response') onResponseReceived?.(event)
    },
    onerror(error) {
      throw error
    },
  })
  if (!terminalEvent) {
    throw new Error('Docchat did not return a response event.')
  }
  onEvent?.(terminalEvent)
  if (terminalEvent.type === 'error') {
    throw new Error(terminalEvent.message)
  }
  return terminalEvent
}
